/**
 * Tracks team impersonation state and exposes stop (reloads the app on success).
 *
 * Where: layouts or banners that must show impersonation and allow exit.
 * Uses: `teamApi.getImpersonationStatus`, `teamApi.stopImpersonation`.
 * Behavior: loads status on mount; `stopImpersonation` forces full page reload to refresh auth context.
 */
import { useState, useEffect, useCallback } from 'react';
import { teamApi } from '@/api/team';

interface ImpersonationState {
  isImpersonating: boolean;
  loading: boolean;
  error: string | null;
}

export const useImpersonation = () => {
  const [state, setState] = useState<ImpersonationState>({
    isImpersonating: false,
    loading: false,
    error: null,
  });

  const checkImpersonationStatus = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await teamApi.getImpersonationStatus();
      setState({
        isImpersonating: response.output === true,
        loading: false,
        error: null,
      });
    } catch (error) {
      setState({
        isImpersonating: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to check impersonation status',
      });
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await teamApi.stopImpersonation();
      setState({
        isImpersonating: false,
        loading: false,
        error: null,
      });
      // Refresh the page to update the user context
      window.location.reload();
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to stop impersonation',
      }));
    }
  }, []);

  useEffect(() => {
    checkImpersonationStatus();
  }, [checkImpersonationStatus]);

  return {
    ...state,
    checkImpersonationStatus,
    stopImpersonation,
  };
};
